import { Button, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import spuServices from "../../services/spu";
import useSpuInfo from "../../stores/spuInfo";
import useSpuPdfParam from "../../stores/spuPdfParam";
import useSpuPoints from "../../stores/spuPoints";
import PerformanceChartPrint from "./PerformanceChartPrint";
import PerformanceResult from "../Spu/Performance/PerformanceResult";
import useDisplayUnits from "../SpuChart/displayUnits/useDisplayUnits";
import {
  convertFlowToCanonical,
  convertHeadToCanonical,
  getDisplayUnitLabel,
} from "../SpuChart/displayUnits/converters";

import "./index.css";

type TemplateField = {
  key?: string;
  value?: string;
};

type NativePreviewData = {
  header?: {
    logo?: string;
    text?: {
      one?: string;
      two?: string;
    };
  };
  footer?: {
    href?: {
      website?: string;
    };
  };
  content?: {
    pageOne?: Record<string, TemplateField>;
  };
};

const findTemplateFieldValue = (fields: TemplateField[], matcher: (key: string) => boolean) => {
  const item = fields.find((field) => matcher((field.key || "").toLowerCase()));
  return item?.value || "";
};

const NativeReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const printedRef = useRef(false);
  const { displayUnits } = useDisplayUnits(searchParams, setSearchParams);
  const [previewData, setPreviewData] = useState<NativePreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const { fetch: fetchInfo, spu, category, image2x, info: spuInfo, loading: infoLoading } = useSpuInfo();
  const {
    fetch: fetchPdfParameter,
    company,
    filename,
    margin,
    loading: pdfParamLoading,
  } = useSpuPdfParam();
  const {
    fetch: fetchPoints,
    name,
    info,
    dataset,
    loading: pointsLoading,
    performance,
    setPerformanceValue,
  } = useSpuPoints();

  const quantity = searchParams.get("q");
  const head = searchParams.get("h");
  const sg = searchParams.get("sg");
  const viscosity = searchParams.get("viscosity");
  const shouldAutoPrint = searchParams.get("print") === "1";

  useEffect(() => {
    if (!id) {
      return;
    }

    fetchInfo(id);
    fetchPoints(id);
    fetchPdfParameter(id);
  }, [fetchInfo, fetchPdfParameter, fetchPoints, id]);

  useEffect(() => {
    if (!id) {
      return;
    }

    let cancelled = false;

    const loadPreviewData = async () => {
      try {
        setPreviewLoading(true);
        const res = await spuServices.getPdfPreviewData(Number(id), 1);
        if (!cancelled) {
          setPreviewData(res?.data?.response || null);
        }
      } catch (error) {
        if (!cancelled) {
          setPreviewData(null);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    loadPreviewData();

    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!dataset) {
      return;
    }

    const parsedQuantity = quantity ? Number(quantity) : undefined;
    const parsedHead = head ? Number(head) : undefined;
    const finalQuantity =
      parsedQuantity !== undefined
        ? (convertFlowToCanonical(parsedQuantity, displayUnits.flow) as number)
        : undefined;
    const finalHead =
      parsedHead !== undefined
        ? (convertHeadToCanonical(parsedHead, displayUnits.head) as number)
        : undefined;
    const lsQEffType = window.localStorage.getItem("qEffType");

    setPerformanceValue({
      type: 0,
      quantity: finalQuantity,
      head: finalHead,
      sg: sg ? Number(sg) : 1,
      qeff: lsQEffType ? Number(lsQEffType) : 3,
      viscosity: viscosity ? Number(viscosity) : undefined,
      viscosityUnit: displayUnits.viscosity,
    });
  }, [dataset, displayUnits.flow, displayUnits.head, displayUnits.viscosity, head, quantity, setPerformanceValue, sg, viscosity]);

  useEffect(() => {
    if (!shouldAutoPrint || printedRef.current || !dataset) {
      return;
    }

    printedRef.current = true;
    window.setTimeout(() => {
      window.print();
    }, 400);
  }, [dataset, shouldAutoPrint]);

  const pageOneData = useMemo(() => previewData?.content?.pageOne || {}, [previewData]);
  const pageOneFields = useMemo(
    () =>
      Object.keys(pageOneData)
        .filter((key) => key !== "cateBImg" && key !== "cateContent")
        .map((key) => pageOneData[key])
        .filter(Boolean),
    [pageOneData]
  );
  const coverImage = pageOneData?.cateBImg?.value || image2x || spuInfo?.bImg || "";
  const descriptionHtml = pageOneData?.cateContent?.value || spuInfo?.cate_content || category?.content || "";
  const modelValue =
    findTemplateFieldValue(pageOneFields, (key) => key === "model" || key.includes("model")) ||
    filename ||
    name ||
    spu ||
    t("route.chart");
  const reportTitle = modelValue;
  const reportDate = company?.date || "-";
  const reportIssuer = company?.founder || company?.name || "-";
  const templateHeader = previewData?.header;
  const templateFooter = previewData?.footer;
  const reportMeta = useMemo(
    () => [
      { label: t("spuInfo.pdfExport.company.date"), value: reportDate },
      { label: t("spuInfo.pdfExport.company.founder"), value: reportIssuer },
      { label: t("chart.frequency"), value: info?.frequency ? `${info.frequency}hz` : "-" },
      { label: t("chart.speed"), value: info?.speed ? `${info.speed}rpm` : "-" },
      {
        label: t("chart.diameter"),
        value: info?.diameter ? `${info.diameter}${getDisplayUnitLabel("diameter", displayUnits.diameter)}` : "-",
      },
      {
        label: "Q",
        value: quantity ? `${quantity}${getDisplayUnitLabel("flow", displayUnits.flow)}` : "-",
      },
      {
        label: "H",
        value: head ? `${head}${getDisplayUnitLabel("head", displayUnits.head)}` : "-",
      },
    ],
    [displayUnits.diameter, displayUnits.flow, displayUnits.head, head, info?.diameter, info?.frequency, info?.speed, quantity, reportDate, reportIssuer, t]
  );
  const paperPaddingStyle = {
    paddingTop: `${margin?.top ?? 18}mm`,
    paddingRight: `${margin?.right ?? 16}mm`,
    paddingBottom: `${margin?.bottom ?? 18}mm`,
    paddingLeft: `${margin?.left ?? 16}mm`,
  };

  const loading = infoLoading || pointsLoading || pdfParamLoading || previewLoading || !dataset;
  const pageOneMidpoint = Math.ceil(pageOneFields.length / 2);
  const leftFields = pageOneFields.slice(0, pageOneMidpoint);
  const rightFields = pageOneFields.slice(pageOneMidpoint);

  return (
    <div className="native-report-page">
      <div className="native-report-toolbar">
        <Button onClick={() => navigate(-1)}>{t("confirm.cancel")}</Button>
        <Button type="primary" onClick={() => window.print()}>
          {t("nativePdf.printButton")}
        </Button>
      </div>

      <div className="native-report-paper" style={paperPaddingStyle}>
        {loading ? (
          <div className="native-report-loading">
            <Spin />
          </div>
        ) : (
          <>
            <header className="native-report-header template-one-header">
              <div className="template-one-header-left">
                {templateHeader?.logo ? <img src={templateHeader.logo} alt="" className="template-one-header-logo" /> : null}
              </div>
              <div className="template-one-header-right">
                <h1>{templateHeader?.text?.one || company?.name || category?.name || ""}</h1>
                <p>{templateHeader?.text?.two || ""}</p>
              </div>
            </header>

            <section className="native-report-section native-report-section-cover">
              <div className="native-report-eyebrow">{t("nativePdf.reportEyebrow")}</div>
              <div className="template-one-model">{reportTitle}</div>
              <div className="template-one-cover-grid">
                <div className="template-one-cover-column">
                  {leftFields.map((item, index) => (
                    <div key={`cover-left-${index}`} className="template-one-cover-field">
                      <div className="template-one-cover-label">{item?.key}</div>
                      {item?.value ? <div className="template-one-cover-value">{item.value}</div> : null}
                    </div>
                  ))}
                </div>
                <div className="template-one-cover-column">
                  {rightFields.map((item, index) => (
                    <div key={`cover-right-${index}`} className="template-one-cover-field">
                      <div className="template-one-cover-label">{item?.key}</div>
                      {item?.value ? <div className="template-one-cover-value">{item.value}</div> : null}
                    </div>
                  ))}
                </div>
              </div>
              <div className="native-report-cover">
                {coverImage ? <img src={coverImage} alt={reportTitle} /> : <div className="native-report-cover-empty" />}
              </div>
            </section>

            <section className="native-report-section">
              <div className="native-report-section-title">{t("spuChart.tab.description")}</div>
              <div
                className="native-report-description"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />
            </section>

            <section className="native-report-section">
              <div className="native-report-section-title">{t("spuChart.tab.performance")}</div>
              <div className="native-report-meta native-report-performance-meta">
                {reportMeta.map((item) => (
                  <div key={item.label} className="native-report-meta-item">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </div>
                ))}
              </div>
              <div className="native-report-chart">
                <PerformanceChartPrint
                  key={`${reportTitle}-native-report`}
                  name={reportTitle}
                  info={info}
                  dataset={dataset}
                  performance={performance.fieldsValue}
                  displayUnits={displayUnits}
                  renderType="popover"
                />
              </div>
              <div className="native-report-result">
                <PerformanceResult fieldsResult={performance.fieldsResult} displayUnits={displayUnits} />
              </div>
            </section>

            <footer className="native-report-footer template-one-footer">
              <div>{company?.phone || ""}</div>
              <div>1</div>
              <div>{templateFooter?.href?.website || company?.email || ""}</div>
            </footer>
          </>
        )}
      </div>
    </div>
  );
};

export default NativeReport;
